import { useState, useRef, useEffect, forwardRef } from "react";
import { IconClock } from "@tabler/icons-react";
import { clsx } from "clsx";
import { Text } from "../typography";
import { Button } from "../button";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function parseValue(value: string): { hour: number; minute: number } {
  const [h, m] = value.split(":").map((p) => Number(p));
  return {
    hour: Number.isFinite(h) ? Math.max(0, Math.min(23, h)) : 12,
    minute: Number.isFinite(m) ? Math.max(0, Math.min(59, m)) : 0,
  };
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** `md` (default, h-10) matches compact form controls; `lg` (h-[54px]) matches the default `<Input>` height. */
  size?: "md" | "lg";
}

export function TimePicker({ value, onChange, label, size = "md" }: TimePickerProps) {
  const { hour, minute } = parseValue(value || "12:00");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Center the selected hour/minute when the popover opens.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      [hoursRef, minutesRef].forEach((r) => {
        const sel = r.current?.querySelector<HTMLElement>("[data-selected='true']");
        if (sel && r.current) {
          r.current.scrollTop = sel.offsetTop - r.current.clientHeight / 2 + sel.clientHeight / 2;
        }
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  function setHour(h: number) {
    onChange(`${pad(h)}:${pad(minute)}`);
  }

  function setMinute(m: number) {
    onChange(`${pad(hour)}:${pad(m)}`);
  }

  function selectNow() {
    const now = new Date();
    onChange(`${pad(now.getHours())}:${pad(now.getMinutes())}`);
  }

  return (
    <div className="flex flex-col gap-1.5" ref={wrapperRef}>
      {label && (
        <button type="button" className="px-1.5 text-left" onClick={() => setOpen((v) => !v)}>
          <Text variant="bodySmBold" tone="body" inline>
            {label}
          </Text>
        </button>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={clsx(
            "flex w-full items-center gap-2 rounded-2xl border border-borderDefault bg-bgWhite px-4 cursor-pointer shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
            size === "lg" ? "h-[54px]" : "h-10",
          )}
        >
          <IconClock size={16} className="shrink-0 text-textBody" />
          <Text variant="bodySmBold" tone="dark" inline>
            {`${pad(hour)}:${pad(minute)}`}
          </Text>
        </button>

        {open && (
          <div className="absolute top-12 left-0 z-50 w-[220px] rounded-2xl bg-bgWhite border border-borderLight shadow-[0px_8px_32px_rgba(0,0,0,0.1)] p-3">
            <div className="grid grid-cols-2 gap-2">
              <TimeColumn
                ref={hoursRef}
                items={HOURS}
                selected={hour}
                onSelect={setHour}
                ariaLabel="Hours"
              />
              <TimeColumn
                ref={minutesRef}
                items={MINUTES}
                selected={minute}
                onSelect={setMinute}
                ariaLabel="Minutes"
              />
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-borderLight">
              <button type="button" onClick={selectNow}>
                <Text variant="bodySm" tone="accent" inline>
                  Now
                </Text>
              </button>
              <Button variant="filledDark" size="sm" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface TimeColumnProps {
  items: number[];
  selected: number;
  onSelect: (n: number) => void;
  ariaLabel: string;
}

const TimeColumn = forwardRef<HTMLDivElement, TimeColumnProps>(function TimeColumn(
  { items, selected, onSelect, ariaLabel },
  ref,
) {
  return (
    <div
      ref={ref}
      role="listbox"
      aria-label={ariaLabel}
      className="max-h-[200px] overflow-y-auto rounded-xl"
    >
      <div className="flex flex-col">
        {items.map((n) => {
          const isSelected = n === selected;
          return (
            <button
              key={n}
              type="button"
              role="option"
              aria-selected={isSelected}
              data-selected={isSelected ? "true" : "false"}
              onClick={() => onSelect(n)}
              className={clsx(
                "h-9 flex items-center justify-center rounded-2xl transition-colors shrink-0",
                isSelected ? "bg-textDark text-bgWhite" : "text-textDark hover:bg-bgLight",
              )}
            >
              <Text variant="body" inline tone="inherit">
                {pad(n)}
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
});
